/**
 * Geolocalización para el reporte de plagas (Fase 5 T-B, 2026-08-03).
 *
 * HÍBRIDA desde 2026-08-04: en la app nativa (wrapper Despia) la fuente
 * primaria es el puente `location://simple` — la API web dentro de la WebView
 * solo muestra el diálogo de permiso si el contenedor lo tiene cableado, y no
 * lo está: el trabajador no veía NINGÚN prompt. El puente lo pide desde la
 * capa nativa y el diálogo "Al usar la app" sale seguro. La API web sigue
 * corriendo como respaldo y como único detector de denegación (Despia no
 * expone el estado del permiso). El fix nativo caduca (~30 s), así que se
 * repregunta periódicamente mientras el wizard está abierto.
 *
 * watchPosition con alta precisión desde que se monta (el primer fix tarda;
 * pedirlo al enviar sería bloquear al worker mirando un spinner). Decisión de
 * producto: SIN posición no se puede enviar el muestreo (isBlocking); una
 * precisión mala (>50 m, normal bajo plástico) NO bloquea — se guarda la
 * accuracy y el mapa pinta el foco como aproximado.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { canUseNativeLocation, getDespiaFix } from "@/lib/geo/despiaLocation";

export type GeoStatus = "idle" | "requesting" | "watching" | "denied" | "unavailable" | "timeout";

export interface GeoPosition {
  lat: number;
  lng: number;
  accuracyM: number;
  timestamp: number;
}

export interface GeoState {
  status: GeoStatus;
  position: GeoPosition | null;
  /** true ⇒ el envío debe quedar deshabilitado (sin posición utilizable). */
  isBlocking: boolean;
  /** Estado del permiso según la Permissions API (null si no soportada).
   *  ⚠️ Solo PISTA: iOS/WKWebView responde "prompt" aunque esté denegado. */
  permission: "granted" | "denied" | "prompt" | null;
  /** La app corre EMBEBIDA (iframe, p.ej. la preview de Lovable): el iframe
   *  puede bloquear la geolocalización sin llegar a preguntar. */
  inIframe: boolean;
}

const FIX_TIMEOUT_MS = 20_000;
/** El fix del puente de Despia caduca ~30 s: repreguntar a mitad de vida. */
const NATIVE_REFRESH_MS = 15_000;
/** Un fix con más de esto NO desbloquea el envío: podría ser de otra nave o
 *  de camino a la finca (hallazgo adversarial 2026-08-04). */
const STALE_FIX_MS = 60_000;
/** Precisión sustituta cuando el puente nativo no la declara: Infinity no
 *  sobrevive al JSON (→ null en BD = "precisa") y pintaba ±Infinity en la UI.
 *  999 = "muy mala": el mapa marca aproximado y la regla de perímetro no acusa. */
const UNKNOWN_ACCURACY_M = 999;

/** ¿El fix nuevo mejora al actual? Más reciente en >5 s siempre gana; si son
 *  coetáneos, gana el más preciso. */
function isBetterFix(next: GeoPosition, current: GeoPosition | null): boolean {
  if (!current) return true;
  if (next.timestamp > current.timestamp + 5_000) return true;
  if (next.timestamp < current.timestamp - 5_000) return false;
  return next.accuracyM <= current.accuracyM;
}

export function useGeolocation(opts: { enabled: boolean }): GeoState & { retry: () => void } {
  const { enabled } = opts;
  const [status, setStatus] = useState<GeoStatus>("idle");
  const [position, setPosition] = useState<GeoPosition | null>(null);
  const [permission, setPermission] = useState<"granted" | "denied" | "prompt" | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const positionRef = useRef<GeoPosition | null>(null);
  useEffect(() => {
    positionRef.current = position;
  }, [position]);
  const [attempt, setAttempt] = useState(0);
  const inIframe = typeof window !== "undefined" && window.self !== window.top;

  const acceptFix = useCallback((fix: GeoPosition) => {
    setPosition((current) => (isBetterFix(fix, current) ? fix : current));
    setStatus("watching");
  }, []);

  // Estado real del permiso (para mensajes precisos: denegado vs sin preguntar).
  useEffect(() => {
    if (!enabled || typeof navigator === "undefined" || !navigator.permissions?.query) return;
    let cancelled = false;
    navigator.permissions
      .query({ name: "geolocation" as PermissionName })
      .then((res) => {
        if (cancelled) return;
        setPermission(res.state as "granted" | "denied" | "prompt");
        res.onchange = () => setPermission(res.state as "granted" | "denied" | "prompt");
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [enabled, attempt]);

  // Puente nativo de Despia: fuente primaria en la app nativa. La primera
  // llamada dispara el diálogo de permiso del sistema si aún no se concedió.
  useEffect(() => {
    if (!enabled || !canUseNativeLocation()) return;
    let cancelled = false;
    let misses = 0;
    const pull = async () => {
      const fix = await getDespiaFix();
      if (cancelled) return;
      if (!fix) {
        // Puente mudo (wrapper viejo, permiso denegado a nivel de sistema —
        // Despia no lo reporta): sin esto, en una WebView sin geo web el
        // status se quedaba en "idle/buscando" para siempre y el banner ni
        // siquiera enseñaba el botón de reintento (callejón sin salida).
        misses += 1;
        if (misses >= 2 && positionRef.current == null) setStatus("unavailable");
        return;
      }
      misses = 0;
      acceptFix({
        lat: fix.lat,
        lng: fix.lng,
        accuracyM: Number.isFinite(fix.accuracyM) ? fix.accuracyM : UNKNOWN_ACCURACY_M,
        timestamp: Date.now(),
      });
    };
    void pull();
    const timer = setInterval(() => void pull(), NATIVE_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [enabled, attempt, acceptFix]);

  useEffect(() => {
    if (!enabled) {
      setStatus("idle");
      return;
    }
    // Al (re)activar, un fix heredado de la apertura anterior no vale: el
    // trabajador puede haber caminado a otra nave con el wizard desmontado
    // solo visualmente (los tres montajes son permanentes con open=false).
    if (positionRef.current && Date.now() - positionRef.current.timestamp > STALE_FIX_MS) {
      positionRef.current = null;
      setPosition(null);
    }
    if (!("geolocation" in navigator)) {
      if (!canUseNativeLocation()) setStatus("unavailable");
      else setStatus("requesting");
      return;
    }
    setStatus("requesting");
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        acceptFix({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracyM: pos.coords.accuracy,
          timestamp: pos.timestamp,
        });
      },
      (err) => {
        // Con un fix RECIENTE, un fallo de la API web no degrada nada (en la
        // app nativa la web puede estar sin cablear y fallar siempre). Pero
        // un fix viejo NO salva del error: si el GPS muere a mitad de flujo,
        // conservar coordenadas rancias desbloqueaba el envío con la
        // posición de hace media hora (hallazgo adversarial 2026-08-04).
        const cur = positionRef.current;
        if (cur && Date.now() - cur.timestamp <= STALE_FIX_MS) return;
        if (cur) {
          positionRef.current = null;
          setPosition(null);
        }
        setStatus(
          err.code === err.PERMISSION_DENIED
            ? "denied"
            : err.code === err.TIMEOUT
              ? "timeout"
              : "unavailable",
        );
      },
      { enableHighAccuracy: true, timeout: FIX_TIMEOUT_MS, maximumAge: 15_000 },
    );
    watchIdRef.current = id;
    return () => {
      if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    };
  }, [enabled, attempt, acceptFix]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return {
    status,
    position,
    isBlocking: enabled && position === null,
    permission,
    inIframe,
    retry,
  };
}
